import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { getFirestore, doc, getDoc, updateDoc } from 'firebase/firestore';
import { DesignProvider } from './hooks/DesignContext';
import { AnimatePresence } from 'framer-motion';
import Decor from './pages/design/decor/Decor.js';
import Explore from './pages/explore/Explore.js';
import LoginPage from './pages/login/LoginPage.js';
import UserProfile from './pages/profile/UserProfile.js';
import PrivacyPolicy from './pages/privacyPolicy/PrivacyPolicy.jsx';
import TermsOfService from './pages/termsOfService/TermsOfService.jsx';
import PricingPlan from './pages/pricingPlan/PricingPlan.jsx';
import './App.css';
import DesignOptions from './pages/design/main/DesignOptions.js';
import Florals from './pages/design/floral/Florals.js';
import WeddingCakes from './pages/design/weddingCake/WeddingCakes.js';
import { fetchCredits } from './utils/credits';
import Proposals from './pages/design/proposal/Proposals.js';
import WeddingAttire from './pages/design/attire/WeddingAttire.js';
import PlanPage from './pages/plan/Plan.tsx';
import { TaskProvider } from './context/TaskContext.tsx';
import { BudgetProvider } from './context/BudgetContext.tsx';
import VendorOnboarding from './pages/onboarding/VendorOnboarding';
import Connect from './pages/connect/Connect.tsx';
import VendorDetailPage from './pages/connect/VendorDetailPage.tsx';
import SearchResultsPage from './pages/connect/components/SearchResultsPage.tsx';
import ForProfessionals from './pages/ForProfessionals.tsx';
import VendorPage from './pages/VendorPage.tsx';
import useNavBlur from './hooks/useNavBlur.js';
import { navigationService } from './services/navigationService.ts';

import Navbar from './components/Navbar.tsx';
import LeftSidebar from './components/LeftSidebar.tsx';
import Hero from './components/Hero.tsx';
import Features from './components/Features.tsx';
import HowItWorks from './components/HowItWorks.tsx';
import AIImageGeneration from './components/AIImageGeneration.tsx';
import PlanningSection from './components/PlanningSection.tsx';
import VendorSection from './components/VendorSection.tsx';
import ComingSoonSection from './components/ComingSoonSection.tsx';
import Footer from './components/Footer.tsx';
import { GlobalChatbotWithContext, ChatbotProvider, useChatbot } from './components/chat/GlobalChatbot.tsx';
import { HelmetProvider } from 'react-helmet-async';


// ScrollToTop component
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    // Eventually need to not use the root id
    document.getElementById('root').scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// ScrollToSection component
const ScrollToSection = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.state?.scrollTo) {
      const element = document.getElementById(location.state.scrollTo);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location]);

  return null;
};

const AppContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null);
  const [credits, setCredits] = useState(null);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Get chatbot state from context (now available from higher level)
  const { isOpen: isChatOpen, openChat, closeChat } = useChatbot();

  const mainContentRef = useRef(null);
  
  // Set up navigation service
  useEffect(() => {
    navigationService.setNavigate(navigate);
    console.log('🔄 Navigation service initialized');
  }, [navigate]);
  
  // Pages that shouldn't show any navigation
  const noNavPages = ['/login', '/privacy-policy', '/terms-of-service', '/pricing-plan'];
  const shouldShowNavigation = !noNavPages.includes(location.pathname);
  
  // Show top navbar only when not logged in and navigation should be shown
  const shouldShowTopNavbar = shouldShowNavigation && !user;
  
  // Show left sidebar only when logged in and navigation should be shown
  const shouldShowLeftSidebar = shouldShowNavigation && user;


  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleRouteChange = () => {
      // Don't reset scroll state on route changes
      setIsInitialized(true);
    };

    window.addEventListener('popstate', handleRouteChange);
    return () => window.removeEventListener('popstate', handleRouteChange);
  }, []);

  useEffect(() => {
    const auth = getAuth();
    const db = getFirestore();

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        if (!currentUser.emailVerified) {
          console.log('Email not verified');
        }
        setUser(currentUser);
        fetchCredits(currentUser);

        try {
          const userDocRef = doc(db, 'users', currentUser.uid);
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            setUserType(userData.userType);

            if (currentUser.emailVerified !== userData.emailVerified) {
              await updateDoc(userDocRef, {
                emailVerified: currentUser.emailVerified,
              });
            }
          }
        } catch (err) {
          setError('Failed to fetch user data');
          setUserType(null);
        }
      } else {
        setUser(null);
        setUserType(null);
        setCredits(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <TaskProvider>
      <BudgetProvider>
        <DesignProvider>
          <div className="app">
            <ScrollToTop />
            <ScrollToSection />
            
            {/* Top Navbar - Only shown when not logged in */}
            {shouldShowTopNavbar && (
              <Navbar scrollableElementRef={{ current: document.getElementById('root') }} />
            )}
            
                        {/* Left Sidebar - Only shown when logged in */}
            {shouldShowLeftSidebar && <LeftSidebar />}
            
            {/* Main Header - Always visible when logged in */}
            {shouldShowLeftSidebar && (
              <div className="fixed top-0 left-0 right-0 z-[10002] flex items-center justify-between w-full px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-slate-50 to-gray-50 shadow-sm h-[72px]">
                <button 
                  onClick={() => navigate('/')}
                  className="flex items-center space-x-3 hover:opacity-80 transition-opacity duration-200"
                >
                  <img 
                    src="/logos/full_band_1.svg" 
                    alt="Wedify Logo" 
                    className="w-10 h-10 drop-shadow-lg filter brightness-110"
                  />
                  <span className="text-3xl font-bold text-gray-900">Wedify</span>
                </button>
                
                {user && (
                  <div className="flex items-center space-x-3">
                    {/* Profile Button */}
                    <button
                      onClick={() => navigate('/user-profile')}
                      className="flex items-center space-x-2 px-3 py-2 bg-white/60 hover:bg-white/80 rounded-lg border border-gray-200/50 shadow-sm transition-all duration-200"
                    >
                      <div className="w-6 h-6 bg-gradient-to-br from-[#FF4E8E] to-[#FF7F50] rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-semibold">
                          {user.email?.charAt(0).toUpperCase() || 'U'}
                        </span>
                      </div>
                      <span className="text-sm font-medium text-gray-700">
                        {user.displayName || user.email?.split('@')[0] || 'User'}
                      </span>
                    </button>
                    
                    {/* Logout Button */}
                    <button
                      onClick={async () => {
                        try {
                          const auth = getAuth();
                          await signOut(auth);
                          navigate('/');
                        } catch (error) {
                          console.error('Logout error:', error);
                        }
                      }}
                      className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg border border-gray-200/50 shadow-sm transition-all duration-200"
                    >
                      Log out
                    </button>
                  </div>
                )}
              </div>
            )}
 

            {/* Main Layout Container */}
            <div className={`flex h-screen ${shouldShowTopNavbar ? 'pt-[72px]' : shouldShowLeftSidebar ? 'pt-[72px]' : ''}`}>
              {/* Left Sidebar Space - Account for sidebar when shown */}
              {shouldShowLeftSidebar && <div className="w-16 flex-shrink-0" />}
              
              {/* Chat Panel Space - Reserved but panel renders via portal */}
              {isChatOpen && <div className="w-[420px] flex-shrink-0" />}
            
              {/* Preview Pane - Main Content */}
              <div className={`flex-1 overflow-hidden ${shouldShowLeftSidebar ? 'bg-gradient-to-b from-slate-50 to-white' : 'bg-white border-l-2 border-gray-200/80 shadow-xl'}`}>
                <div 
                  className="overflow-y-auto bg-gradient-to-b from-slate-50 to-white" 
                  style={{ height: shouldShowLeftSidebar ? 'calc(100vh - 72px)' : 'calc(100vh - 140px)' }} 
                  id="preview-pane"
                >
              <div className="app-container" ref={mainContentRef}>
                    <main className="w-full min-h-full">
                {error && <div style={{ color: 'red' }}>{error}</div>}
                <AnimatePresence mode="wait">
                  <Routes>
                    <Route
                      path="/"
                      element={
                          <div className="min-h-screen text-text-primary overflow-y-auto">
                            {Hero && <Hero />}
                            {Features && <Features />}
                            {HowItWorks && <HowItWorks />}
                            {AIImageGeneration && <AIImageGeneration />}
                            {PlanningSection && <PlanningSection />}
                            {VendorSection && <VendorSection />}
                            {ComingSoonSection && <ComingSoonSection />}
                          </div>
                      }
                    />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/terms-of-service" element={<TermsOfService />} />
                    <Route path="/pricing-plan" element={<PricingPlan />} />
                    <Route
                      path="/user-profile"
                      element={
                            user ? (
                            <UserProfile user={user} userType={userType} />
                          ) : (
                            <Navigate to="/login" />
                            )
                      }
                    />
                    <Route
                      path="/design"
                          element={user ? <DesignOptions /> : <Navigate to="/login" />}
                    />
                    <Route
                      path="/design/decor"
                      element={
                            user ? (
                            <Decor
                              userType={userType}
                              credits={credits}
                              setCredits={setCredits}
                              showCreditsModal={showCreditsModal}
                              setShowCreditsModal={setShowCreditsModal}
                            />
                          ) : (
                            <Navigate to="/login" />
                            )
                      }
                    />
                    <Route
                      path="/design/florals"
                          element={user ? <Florals userType={userType} /> : <Navigate to="/login" />}
                    />
                    <Route
                      path="/design/weddingcake"
                          element={user ? <WeddingCakes /> : <Navigate to="/login" />}
                    />
                    <Route
                      path="/design/proposals"
                          element={user ? <Proposals /> : <Navigate to="/login" />}
                    />
                    <Route
                      path="/design/attire"
                          element={user ? <WeddingAttire /> : <Navigate to="/login" />}
                    />
                    <Route
                      path="/explore"
                      element={user ? <Explore /> : <Navigate to="/login" />}
                    />
                    <Route 
                      path="/plan" 
                          element={user ? <PlanPage /> : <Navigate to="/login" />}
                    />
                    <Route path="/design" element={<DesignOptions />} />
                    <Route path="/explore" element={<Explore />} />
                    <Route path="/vendors" element={VendorPage ? <VendorPage /> : <div>Loading...</div>} />
                    
                    <Route
                      path="/connect"
                          element={user ? <Connect /> : <Navigate to="/login" />}
                        />
                        <Route
                          path="/connect/search"
                          element={user ? <SearchResultsPage /> : <Navigate to="/login" />}
                    />
                    <Route
                      path="/connect/vendor/:vendorId/:vendorName"
                          element={user ? <VendorDetailPage /> : <Navigate to="/login" />}
                    />
                    <Route
                      path="/vendor-onboarding"
                      element={
                            user ? (
                            userType === 'vendor' ? (
                              <VendorOnboarding />
                            ) : (
                              <Navigate to="/" />
                            )
                          ) : (
                            <Navigate to="/login" />
                            )
                      }
                    />
                  </Routes>
                </AnimatePresence>
                </main>
                {/* Render Footer on landing page only */}
                {location.pathname === '/' && !isMobile && <Footer />}
              </div>
                </div>
              </div>
            </div>
          </div>
        </DesignProvider>
      </BudgetProvider>
    </TaskProvider>
  );
};

const App = () => {
  return <AppContent />;
};

const AppWrapper = () => (
  <Router>
    <HelmetProvider>
      <ChatbotProvider>
        {/* Global Chatbot at the highest level - persists through all navigation */}
        <GlobalChatbotWithContext />
        <App />
      </ChatbotProvider>
    </HelmetProvider>
  </Router>
);

export default AppWrapper;